import type { Timestamp } from 'firebase/firestore';
import {
  BadgeCategory,
  BadgeId,
  BadgeProgress,
  DailyEntryRecord,
  EarnedBadge,
  TrackerSettingsRecord,
} from '../types/dailyBite';

/**
 * Narrative badges — we define these in v1 so the system feels intentional
 * rather than random. Correlation-earned badges (e.g. "caffeine × sleep
 * unlocked") arrive in phase 3 when the analysis engine lands.
 *
 * Rules for adding a badge:
 * 1. Must be achievable in ≤30 days of normal use — no 100-day grinds.
 * 2. Earned language is a sentence, not a trophy ("Two honest weeks", not "Level 7").
 * 3. Describe what the member did, never what they "won".
 * 4. Identity badges require the tracker to be OPTED OUT (not just unlogged).
 */

export interface BadgeDefinition {
  id: BadgeId;
  label: string;
  description: string;
  earnedCopy: string;
  category: BadgeCategory;
  targetValue: number;
  /** Compute current progress + earned state from the entries + settings. */
  evaluate: (ctx: BadgeContext) => { earned: boolean; current: number; hint?: string };
}

export interface BadgeContext {
  entries: DailyEntryRecord[];
  entriesByDate: Map<string, DailyEntryRecord>;
  loggedDates: Set<string>;
  settings: TrackerSettingsRecord;
  today: Date;
  last30Keys: string[];
  last14Keys: string[];
}

function lastConsecutiveCount(
  keys: string[],
  predicate: (key: string) => boolean,
): number {
  let count = 0;
  for (let i = keys.length - 1; i >= 0; i -= 1) {
    if (predicate(keys[i])) {
      count += 1;
    } else {
      break;
    }
  }
  return count;
}

function countMatchingDays(keys: string[], predicate: (key: string) => boolean): number {
  let c = 0;
  for (const k of keys) if (predicate(k)) c += 1;
  return c;
}

export const BADGE_DEFINITIONS: BadgeDefinition[] = [
  {
    id: 'first_bite',
    label: 'First bite',
    description: 'Log a single tracker to start your Daily Bite.',
    earnedCopy: 'You opened Daily Bite and made your first entry. Everything starts here.',
    category: 'milestone',
    targetValue: 1,
    evaluate: ({ loggedDates }) => ({
      earned: loggedDates.size >= 1,
      current: Math.min(loggedDates.size, 1),
    }),
  },
  {
    id: 'first_week',
    label: 'First week done',
    description: 'Log at least one tracker every day for seven days in a row.',
    earnedCopy: 'Seven days of showing up. This is where the data starts to tell a story.',
    category: 'milestone',
    targetValue: 7,
    evaluate: ({ last30Keys, loggedDates }) => {
      const streak = lastConsecutiveCount(last30Keys, (k) => loggedDates.has(k));
      return {
        earned: streak >= 7,
        current: Math.min(streak, 7),
        hint: streak < 7 ? `${7 - streak} more in a row` : undefined,
      };
    },
  },
  {
    id: 'first_month',
    label: 'First month done',
    description: 'Log at least one tracker every day for 30 days in a row.',
    earnedCopy: 'Thirty days. A full trend line. Your clinician has real signal to read now.',
    category: 'milestone',
    targetValue: 30,
    evaluate: ({ last30Keys, loggedDates }) => {
      const streak = lastConsecutiveCount(last30Keys, (k) => loggedDates.has(k));
      return {
        earned: streak >= 30,
        current: Math.min(streak, 30),
        hint: streak < 30 ? `${30 - streak} days of momentum` : undefined,
      };
    },
  },
  {
    id: 'honest_logger',
    label: 'Honest logger',
    description: 'Log on 25 of the past 30 days — streaks not required.',
    earnedCopy: 'You logged on 25 of the past 30 days. Not every day has to be perfect.',
    category: 'narrative',
    targetValue: 25,
    evaluate: ({ last30Keys, loggedDates }) => {
      const count = countMatchingDays(last30Keys, (k) => loggedDates.has(k));
      return {
        earned: count >= 25,
        current: Math.min(count, 25),
        hint: count < 25 ? `${25 - count} more logged days` : undefined,
      };
    },
  },
  {
    id: 'hydration_habit',
    label: 'Hydration habit',
    description: 'Log water seven days in a row.',
    earnedCopy: 'A full week of water logged. Small act, big signal.',
    category: 'narrative',
    targetValue: 7,
    evaluate: ({ last30Keys, entriesByDate }) => {
      const streak = lastConsecutiveCount(last30Keys, (k) => {
        const e = entriesByDate.get(k);
        return Boolean(e?.water && e.water.totalMl > 0);
      });
      return {
        earned: streak >= 7,
        current: Math.min(streak, 7),
        hint: streak < 7 ? `${7 - streak} more water-logged days` : undefined,
      };
    },
  },
  {
    id: 'caffeine_aware_2wk',
    label: 'Caffeine aware',
    description: 'Log caffeine (including the time) on 14 of the past 30 days.',
    earnedCopy: 'Two weeks of caffeine awareness. Enough data to start seeing the sleep link.',
    category: 'narrative',
    targetValue: 14,
    evaluate: ({ last30Keys, entriesByDate }) => {
      const count = countMatchingDays(last30Keys, (k) => {
        const e = entriesByDate.get(k);
        return Boolean(e?.caffeine && e.caffeine.totalMg > 0);
      });
      return {
        earned: count >= 14,
        current: Math.min(count, 14),
        hint: count < 14 ? `${14 - count} more caffeine-logged days` : undefined,
      };
    },
  },
  {
    id: 'dry_week',
    label: 'Dry week',
    description: 'Seven consecutive days with zero alcohol units.',
    earnedCopy: 'Seven dry days in a row. A small reset your liver will notice.',
    category: 'narrative',
    targetValue: 7,
    evaluate: ({ last30Keys, entriesByDate }) => {
      const streak = lastConsecutiveCount(last30Keys, (k) => {
        const e = entriesByDate.get(k);
        return Boolean(e?.alcohol && e.alcohol.units === 0);
      });
      return {
        earned: streak >= 7,
        current: Math.min(streak, 7),
        hint: streak < 7 ? `${7 - streak} more dry days` : undefined,
      };
    },
  },
  {
    id: 'mood_moment',
    label: 'Mood moment',
    description: 'Log your mood on seven of the past 14 days.',
    earnedCopy: 'A fortnight of mood check-ins. This is where correlation gets interesting.',
    category: 'narrative',
    targetValue: 7,
    evaluate: ({ last14Keys, entriesByDate }) => {
      const count = countMatchingDays(last14Keys, (k) => {
        const e = entriesByDate.get(k);
        return Boolean(e?.mood?.loggedAt);
      });
      return {
        earned: count >= 7,
        current: Math.min(count, 7),
        hint: count < 7 ? `${7 - count} more mood check-ins` : undefined,
      };
    },
  },
  {
    id: 'gratitude_week',
    label: 'Gratitude week',
    description: 'Log gratitude on seven of the past 14 days.',
    earnedCopy: 'Seven gratitude notes in a fortnight. Kept for you — never shared.',
    category: 'narrative',
    targetValue: 7,
    evaluate: ({ last14Keys, entriesByDate }) => {
      const count = countMatchingDays(last14Keys, (k) => {
        const e = entriesByDate.get(k);
        const text = e?.gratitude?.response?.trim() || '';
        return text.length > 0;
      });
      return {
        earned: count >= 7,
        current: Math.min(count, 7),
        hint: count < 7 ? `${7 - count} more reflections` : undefined,
      };
    },
  },
  {
    id: 'no_nicotine_identity',
    label: "Don't use nicotine",
    description: 'Identity badge — opted out of nicotine tracking plus 14 days of logging.',
    earnedCopy: 'You don’t use nicotine. Your clinician sees this clearly in the quarterly review.',
    category: 'identity',
    targetValue: 14,
    evaluate: ({ settings, loggedDates }) => {
      if (settings.optedIn.nicotine) {
        return { earned: false, current: 0, hint: 'Opt out of the nicotine tracker to earn this' };
      }
      return {
        earned: loggedDates.size >= 14,
        current: Math.min(loggedDates.size, 14),
        hint: loggedDates.size < 14 ? `${14 - loggedDates.size} more logged days` : undefined,
      };
    },
  },
  {
    id: 'no_alcohol_identity',
    label: "Don't drink",
    description: 'Identity badge — opted out of alcohol tracking plus 14 days of logging.',
    earnedCopy: 'You don’t drink. Your clinician sees this clearly in the quarterly review.',
    category: 'identity',
    targetValue: 14,
    evaluate: ({ settings, loggedDates }) => {
      if (settings.optedIn.alcohol) {
        return { earned: false, current: 0, hint: 'Opt out of the alcohol tracker to earn this' };
      }
      return {
        earned: loggedDates.size >= 14,
        current: Math.min(loggedDates.size, 14),
        hint: loggedDates.size < 14 ? `${14 - loggedDates.size} more logged days` : undefined,
      };
    },
  },
];

export const BADGE_LOOKUP: Record<BadgeId, BadgeDefinition> = BADGE_DEFINITIONS.reduce(
  (acc, def) => {
    acc[def.id] = def;
    return acc;
  },
  {} as Record<BadgeId, BadgeDefinition>,
);

export function evaluateBadges(
  ctx: BadgeContext,
  existing: EarnedBadge[],
  now: Timestamp,
): { earned: EarnedBadge[]; upcoming: BadgeProgress[] } {
  const earnedMap = new Map(existing.map((b) => [b.id, b] as const));
  const upcoming: BadgeProgress[] = [];

  for (const def of BADGE_DEFINITIONS) {
    const result = def.evaluate(ctx);
    if (result.earned) {
      const prior = earnedMap.get(def.id);
      if (prior) {
        earnedMap.set(def.id, { ...prior, mostRecentAt: now });
      } else {
        earnedMap.set(def.id, { id: def.id, earnedAt: now, mostRecentAt: now });
      }
    } else if (result.current > 0 || def.category !== 'identity') {
      upcoming.push({
        id: def.id,
        currentValue: result.current,
        targetValue: def.targetValue,
        hint: result.hint,
      });
    }
  }

  upcoming.sort((a, b) => {
    const ap = a.currentValue / a.targetValue;
    const bp = b.currentValue / b.targetValue;
    return bp - ap;
  });

  return {
    earned: Array.from(earnedMap.values()),
    upcoming: upcoming.slice(0, 3),
  };
}
